/**
 * Shift viewing and editing for the two hierarchy screens.
 *
 * Both `/configuration/subordinate` (the org chart) and `/employee/subordinate`
 * (My Team) read the same endpoint and offer the same edit dialog; they differ
 * only in how the shift is drawn. Keeping the hook, the dialog and the colour
 * scale here means the two screens cannot drift into showing different shifts
 * for the same person on the same day.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { DialogModal } from "../ui/dialogModal";
import { Textarea } from "../ui/input";
import { HIERARCHY_SHIFT } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "./ToastMsg";
import { formatTimeRangeToAMPM } from "./CommonFunctions";
import { cn } from "../../utils/tailwindUtils";

export const SHIFT_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Palette for colour-by-shift. Indexed by the shift's position in the legend so
 * the same shift keeps the same colour across both screens and across renders.
 * Deliberately not derived from the shift name — a hash would reshuffle every
 * colour the moment a shift is renamed.
 */
const SHIFT_COLORS = [
    { bg: "#e0f2fe", text: "#075985", ring: "#0ea5e9" }, // sky
    { bg: "#ede9fe", text: "#5b21b6", ring: "#8b5cf6" }, // violet
    { bg: "#dcfce7", text: "#166534", ring: "#22c55e" }, // green
    { bg: "#fef3c7", text: "#92400e", ring: "#f59e0b" }, // amber
    { bg: "#ffe4e6", text: "#9f1239", ring: "#f43f5e" }, // rose
    { bg: "#cffafe", text: "#155e75", ring: "#06b6d4" }, // cyan
    { bg: "#f3e8ff", text: "#6b21a8", ring: "#a855f7" }, // purple
    { bg: "#fee2e2", text: "#991b1b", ring: "#ef4444" }, // red
];

const NEUTRAL = { bg: "#f3f4f6", text: "#4b5563", ring: "#9ca3af" };
const PENDING = { bg: "#fef3c7", text: "#92400e", ring: "#f59e0b" };

/** Colour for one shift id, stable for the life of the legend. */
export const colorForShift = (shiftId, shifts) => {
    if (!shiftId) return NEUTRAL;
    const index = (shifts || []).findIndex((s) => s.id === shiftId);
    if (index < 0) return NEUTRAL;
    return SHIFT_COLORS[index % SHIFT_COLORS.length];
};

/**
 * What to show in a chip for a resolved day, in priority order. A day that is
 * off for a known reason reads as that reason; only an unexplained empty day is
 * "Not set", which is the thing a planner needs to notice.
 */
export const describeDay = (day) => {
    if (!day) return { label: "—", tone: NEUTRAL, title: "No data" };
    if (day.is_holiday) return { label: "Holiday", tone: NEUTRAL, title: "Company holiday" };
    if (day.is_weekoff) return { label: "Week off", tone: NEUTRAL, title: "Week off" };
    if (day.is_leave) {
        return { label: day.leave_label || "Leave", tone: NEUTRAL, title: day.leave_label || "On leave" };
    }
    if (!day.shift_id) {
        return { label: "Not set", tone: PENDING, title: "No shift assigned for this day" };
    }
    const timings = day.shift_timings_from && day.shift_timings_to
        ? ` (${formatTimeRangeToAMPM(day.shift_timings_from, day.shift_timings_to)})`
        : "";
    const sourceNote = {
        override: "Changed for this date",
        roster: "From the published roster",
        default: "Standing shift",
    }[day.source] || "";
    return {
        label: day.shift_name || "Shift",
        title: `${day.shift_name || "Shift"}${timings}${sourceNote ? ` · ${sourceNote}` : ""}`,
        isNight: !!day.is_night_shift,
        isOverride: day.source === "override",
    };
};

/**
 * Fetch the caller's hierarchy shifts for one date.
 *
 * Failure is deliberately quiet. This endpoint sits behind the Roster
 * permission, so a manager who can see the org chart but was never granted
 * Roster gets a 403 — that is a correct answer, not an error, and toasting it
 * on every page load would be noise. The screens simply render no chips.
 */
export const useHierarchyShifts = (date) => {
    const { fetch } = useApiMethods();
    const [state, setState] = useState({
        byEmployee: {},
        shifts: [],
        featureEnabled: false,
        canEditAny: false,
        loading: false,
    });

    const dateKey = date ? dayjs(date).format(SHIFT_DATE_FORMAT) : null;

    const load = useCallback(async (signal) => {
        if (!dateKey) return;
        setState((prev) => ({ ...prev, loading: true }));
        const response = await fetch(HIERARCHY_SHIFT, { date: dateKey }, null, signal);
        if (response?.status === "cancelled") return;

        if (response?.status !== "success") {
            setState({
                byEmployee: {}, shifts: [], featureEnabled: false,
                canEditAny: false, loading: false,
            });
            return;
        }

        const data = response.data || {};
        const byEmployee = {};
        (data.employees || []).forEach((row) => {
            byEmployee[row.employee?.id] = { day: row.day, canEdit: !!row.can_edit };
        });
        setState({
            byEmployee,
            shifts: data.shifts || [],
            featureEnabled: !!data.feature_enabled,
            canEditAny: !!data.can_edit_any,
            loading: false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateKey]);

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    return { ...state, refresh: () => load() };
};

const SCOPE_OPTIONS = [
    { value: "date", label: "This date only", hint: "Overrides the roster for one day" },
    { value: "onward", label: "From this date onward", hint: "Up to 14 days" },
    { value: "default", label: "Change standing shift", hint: "Does not change days already recorded" },
];

/**
 * Assign / clear one employee's shift.
 *
 * `day` is the currently resolved day for that employee, used to prefill and to
 * decide whether "Clear override" is offered — it only makes sense when there
 * is an override to clear.
 */
export const ShiftAssignDialog = ({ open, onClose, employee, date, day, shifts, onSaved }) => {
    const { update } = useApiMethods();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            date: dayjs(date),
            shift: day?.shift_id || null,
            scope: "date",
            through_date: null,
            is_weekoff: !!day?.is_weekoff,
            reason: "",
        });
        setSaving(false);
    }, [open, date, day]);

    const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const shiftOptions = useMemo(
        () => (shifts || []).map((s) => ({
            value: s.id,
            label: s.shift_timings_from
                ? `${s.name} (${formatTimeRangeToAMPM(s.shift_timings_from, s.shift_timings_to)})`
                : s.name,
        })),
        [shifts],
    );

    const submit = async (payload, successFallback) => {
        setSaving(true);
        const response = await update(HIERARCHY_SHIFT, payload);
        setSaving(false);

        if (response?.status === "success") {
            ToastMsg("success", response.message || successFallback);
            // The recompute reports anything it declined to change (a penalty
            // under appeal, an HR-finalised short duty). Surfacing it is the
            // difference between "saved" and "saved, but read this".
            (response.data?.warnings || []).forEach((warning) => ToastMsg("warning", warning));
            onSaved?.();
            onClose?.();
        } else {
            const errors = response?.errors;
            const firstError = errors && Object.values(errors)[0];
            ToastMsg("error", firstError || response?.message || "Failed to update shift");
        }
    };

    const handleSave = () => {
        if (!form.is_weekoff && !form.shift) {
            ToastMsg("error", "Please select a shift or mark the day as week off");
            return;
        }
        if (form.scope === "onward" && !form.through_date) {
            ToastMsg("error", "Please choose the last date this applies to");
            return;
        }
        submit({
            employee: employee?.id,
            date: dayjs(form.date).format(SHIFT_DATE_FORMAT),
            shift: form.is_weekoff ? null : form.shift,
            is_weekoff: !!form.is_weekoff,
            scope: form.scope,
            through_date: form.scope === "onward" && form.through_date
                ? dayjs(form.through_date).format(SHIFT_DATE_FORMAT)
                : undefined,
            reason: form.reason || undefined,
        }, "Shift updated successfully");
    };

    const handleClear = () => submit({
        employee: employee?.id,
        date: dayjs(form.date).format(SHIFT_DATE_FORMAT),
        clear: true,
        reason: form.reason || undefined,
    }, "Shift override cleared");

    const employeeName = employee?.employee_name
        || [employee?.first_name, employee?.last_name].filter(Boolean).join(" ")
        || "Employee";

    return (
        <DialogModal
            open={open}
            onClose={onClose}
            onSave={handleSave}
            title={`Shift · ${employeeName}`}
            saveLabel="Save shift"
            loading={saving}
            width="440px"
            editMode
        >
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        Date <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                        format="DD-MM-YYYY"
                        value={form.date || null}
                        onChange={(value) => set("date", value)}
                        allowClear={false}
                        disabled={saving}
                        className="w-full"
                        size="small"
                    />
                </div>

                <Checkbox
                    checked={!!form.is_weekoff}
                    onChange={(e) => set("is_weekoff", e.target.checked)}
                    disabled={saving}
                    className="text-xs"
                >
                    Mark this day as week off
                </Checkbox>

                {!form.is_weekoff && (
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Shift <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={form.shift || undefined}
                            onChange={(value) => set("shift", value)}
                            options={shiftOptions}
                            placeholder="Select a shift"
                            disabled={saving}
                            showSearch
                            optionFilterProp="label"
                            className="w-full"
                            size="small"
                        />
                    </div>
                )}

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Applies to</label>
                    {SCOPE_OPTIONS.map((option) => (
                        <label key={option.value} className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                            <input
                                type="radio"
                                name="shift-scope"
                                value={option.value}
                                checked={form.scope === option.value}
                                onChange={() => set("scope", option.value)}
                                disabled={saving}
                                className="mt-0.5"
                            />
                            <span>
                                {option.label}
                                <span className="block text-[11px] text-gray-500 dark:text-gray-400">{option.hint}</span>
                            </span>
                        </label>
                    ))}
                </div>

                {form.scope === "onward" && (
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Through <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                            format="DD-MM-YYYY"
                            value={form.through_date || null}
                            onChange={(value) => set("through_date", value)}
                            disabledDate={(current) => form.date && current && current < dayjs(form.date).startOf("day")}
                            disabled={saving}
                            className="w-full"
                            size="small"
                        />
                    </div>
                )}

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Reason</label>
                    <Textarea
                        rows={2}
                        value={form.reason || ""}
                        onChange={(e) => set("reason", e.target.value)}
                        placeholder="Why is the shift changing? Saved with the change for audit."
                        disabled={saving}
                    />
                </div>

                {day?.override_id ? (
                    <button
                        type="button"
                        onClick={handleClear}
                        disabled={saving}
                        className={cn(
                            "self-start text-xs font-medium underline",
                            "text-amber-700 hover:text-amber-800",
                            "dark:text-amber-400 dark:hover:text-amber-300",
                            saving && "opacity-50 cursor-not-allowed",
                        )}
                    >
                        Clear this override and follow the roster again
                    </button>
                ) : null}
            </div>
        </DialogModal>
    );
};

/** Inline chip for a resolved day — used by the My Team list. */
export const ShiftBadge = ({ day, shifts }) => {
    const described = describeDay(day);
    const tone = described.tone || colorForShift(day?.shift_id, shifts);
    return (
        <span
            title={described.title}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ background: tone.bg, color: tone.text, border: `1px solid ${tone.ring}` }}
        >
            {described.label}
            {described.isNight ? <span title="Night shift">🌙</span> : null}
            {described.isOverride ? <span title="Changed for this date">•</span> : null}
        </span>
    );
};
